/**
 * SAM-656 — where a service's opening hours come from: `service.settings.hours_source`.
 *
 * - 'custom' (default, and what a missing key means): the service's own `operating_hours`.
 * - 'business': the business's weekly Schedule (`business.schedule_mon` .. `schedule_sun`, each
 *   `["HH:mm-HH:mm", ...]`) is used when computing availability. Inherited, never copied: the
 *   service keeps its own `operating_hours` stored, so switching back to 'custom' restores them.
 *
 * `businessScheduleToOperatingHours` mirrors the backend's
 * `functions/booking/service_hours.py::business_operating_hours`, so the read-only view and the
 * effective preview show exactly the windows the backend will compute with.
 */
import type { BusinessSchedule } from './businessHours';

export type HoursSource = 'business' | 'custom';

export const HOURS_SOURCES: HoursSource[] = ['business', 'custom'];

/** 'business' only when explicitly stored; anything else reads as 'custom'. */
export function hoursSourceOf(settings: unknown): HoursSource {
  const value = settings && typeof settings === 'object' ? (settings as Record<string, unknown>).hours_source : undefined;
  return value === 'business' ? 'business' : 'custom';
}

/**
 * The `settings.hours_source` entry to save: always for 'business'; for 'custom' only when the
 * service already had the key, so an untouched service keeps its exact pre-SAM-656 payload.
 */
export function hoursSourceForSave(source: HoursSource, existingSettings: unknown): { hours_source?: HoursSource } {
  const hadKey = !!existingSettings && typeof existingSettings === 'object'
    && 'hours_source' in (existingSettings as Record<string, unknown>);
  return source === 'business' || hadKey ? { hours_source: source } : {};
}

const DAYS: Array<[keyof BusinessSchedule, string]> = [
  ['schedule_mon', 'monday'], ['schedule_tue', 'tuesday'], ['schedule_wed', 'wednesday'],
  ['schedule_thu', 'thursday'], ['schedule_fri', 'friday'], ['schedule_sat', 'saturday'],
  ['schedule_sun', 'sunday'],
];

export interface EffectiveDayHours {
  open: string;
  close: string;
  extra_windows?: Array<{ open: string; close: string }>;
}

function toMinutes(token: string): number | null {
  const m = (token || '').trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const mer = (m[3] || '').toUpperCase();
  if (mer === 'PM' && h < 12) h += 12;
  if (mer === 'AM' && h === 12) h = 0;
  if (h === 24 && min === 0) return 1440;
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

const clock = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/**
 * Business Schedule -> `operating_hours` shape. Ranges are sorted (first = open/close, the rest
 * extra_windows); a midnight-crossing range is split into "start-23:59" and "00:00-end" on the
 * next day; "24:00" closes at 23:59. Days without a usable range are omitted (closed).
 */
export function businessScheduleToOperatingHours(business: BusinessSchedule | null | undefined): Record<string, EffectiveDayHours> {
  const perDay: Record<string, Array<[number, number]>> = {};
  DAYS.forEach(([, day]) => { perDay[day] = []; });
  DAYS.forEach(([field, day], index) => {
    const raw = business?.[field];
    if (!Array.isArray(raw)) return;
    raw.forEach((entry) => {
      if (typeof entry !== 'string' || !entry.includes('-')) return;
      const cut = entry.indexOf('-');
      const start = toMinutes(entry.slice(0, cut));
      const end = toMinutes(entry.slice(cut + 1));
      if (start == null || end == null || start === 1440) return;
      if (end === 1440) perDay[day].push([start, 1439]);
      else if (end > start) perDay[day].push([start, end]);
      else {
        perDay[day].push([start, 1439]);
        if (end > 0) perDay[DAYS[(index + 1) % 7][1]].push([0, end]);
      }
    });
  });

  const out: Record<string, EffectiveDayHours> = {};
  DAYS.forEach(([, day]) => {
    const unique = Array.from(new Set(perDay[day].map(([a, b]) => `${a}-${b}`)))
      .map((k) => k.split('-').map(Number) as [number, number])
      .sort((x, y) => x[0] - y[0] || x[1] - y[1]);
    if (!unique.length) return;
    const [first, ...rest] = unique;
    out[day] = {
      open: clock(first[0]),
      close: clock(first[1]),
      ...(rest.length ? { extra_windows: rest.map(([a, b]) => ({ open: clock(a), close: clock(b) })) } : {}),
    };
  });
  return out;
}

/** True when the business has at least one usable range on some weekday. */
export function businessHasHours(business: BusinessSchedule | null | undefined): boolean {
  return Object.keys(businessScheduleToOperatingHours(business)).length > 0;
}

/** Where the business page's Schedule section lives (BusinessPage keeps tab/section in the URL). */
export function businessScheduleHref(businessId: string): string {
  return `/business/${encodeURIComponent(businessId)}?tab=settings&section=schedule`;
}

function to12h(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

/** "9:00 AM – 12:00 PM, 2:00 PM – 5:00 PM", or "Closed". */
export function formatDayHours(day: EffectiveDayHours | undefined): string {
  if (!day) return 'Closed';
  return [{ open: day.open, close: day.close }, ...(day.extra_windows || [])]
    .map((w) => `${to12h(w.open)} – ${to12h(w.close)}`)
    .join(', ');
}
